// Conditional governance rates (scopestack-api RateRuled). A rule's
// conditions must ALL hold; the first matching rule's rate replaces the base
// rate, and a matched 0 rules the governance out. Rates are fractions on the
// wire (0.15 = 15%) and percentages in the UI.

export type RateRuleFact = "effort" | "pricing_model";
export type RateRuleOp = "gt" | "gte" | "lt" | "lte" | "eq" | "ne";

export interface RateRuleCondition {
  fact: RateRuleFact;
  op: RateRuleOp;
  /** Hours for effort, a pricing model key for pricing_model; null = unset. */
  value: number | string | null;
}

export interface RateRule {
  conditions: RateRuleCondition[];
  /** Fraction; null until the user enters one. */
  rate: number | null;
}

export const FACT_OPTIONS: { value: RateRuleFact; label: string }[] = [
  { value: "effort", label: "Hours" },
  { value: "pricing_model", label: "Pricing model" },
];

export const OPS_BY_FACT: Record<RateRuleFact, RateRuleOp[]> = {
  effort: ["gte", "gt", "lte", "lt"],
  pricing_model: ["eq", "ne"],
};

export const OP_LABELS: Record<RateRuleOp, string> = {
  gte: "at least",
  gt: "more than",
  lte: "at most",
  lt: "less than",
  eq: "is",
  ne: "is not",
};

export const PRICING_MODEL_OPTIONS = [
  { value: "time_and_materials", label: "Time and Materials" },
  { value: "fixed_fee", label: "Fixed Fee" },
  { value: "mixed", label: "Mixed" },
  { value: "other", label: "Other" },
];

/** The API rejects a rule rate of 10 (1000%) or more. */
export const MAX_RULE_PERCENT = 999.99;

// Rounded to the API's 4 fraction digits so 0.07 shows as 7, not 7.000000001.
export const fractionToPercent = (fraction: number | null): number | null =>
  fraction == null ? null : Number((fraction * 100).toFixed(2));

export const percentToFraction = (percent: string): number | null =>
  percent === "" ? null : Number((Number(percent) / 100).toFixed(4));

export const newCondition = (
  fact: RateRuleFact = "effort"
): RateRuleCondition => ({ fact, op: OPS_BY_FACT[fact][0], value: null });

export const newRule = (): RateRule => ({
  conditions: [newCondition()],
  rate: null,
});

export const withFact = (
  condition: RateRuleCondition,
  fact: RateRuleFact
): RateRuleCondition =>
  condition.fact === fact ? condition : newCondition(fact);

const numberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

/** Defensive: rate-rules is jsonb, so trust nothing about its shape. */
export const rateRulesFromApi = (raw: unknown): RateRule[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((rule: any) => ({
    conditions: (Array.isArray(rule?.conditions) ? rule.conditions : []).map(
      (condition: any) => ({
        fact: condition?.fact,
        op: condition?.op,
        value:
          condition?.fact === "effort"
            ? numberOrNull(condition?.value)
            : condition?.value ?? null,
      })
    ),
    rate: numberOrNull(rule?.rate),
  }));
};

export const rateRulesToApi = (rules: RateRule[]) =>
  rules.map((rule) => ({
    conditions: rule.conditions.map(({ fact, op, value }) => ({
      fact,
      op,
      value,
    })),
    // The rate input is required, so null never gets here from the form; if
    // it did, dropping the key earns the API's 422 rather than a silent 0.
    rate: rule.rate ?? undefined,
  }));
